import { MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useCallback, useEffect, useState } from 'react';
import { CountryIso2, defaultCountries, FlagEmoji } from 'react-international-phone';
import { twMerge } from 'tailwind-merge';

import useDebounce from '../../hooks/useDebounce';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t } from '../../services/translation/translation';

type Props = {
  country: CountryIso2;
  setCountry: (_country: CountryIso2) => void;
  className?: string;
};

export const CountryPicker = ({ country, className, setCountry }: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 500);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  useEffect(() => {
    if (isVisible === false) {
      setFilter('');
    }
  }, [isVisible]);

  return (
    <div className={twMerge('relative p-2', className)}>
      <button type="button" onClick={() => setIsVisible(true)}>
        <FlagEmoji iso2={country} />
      </button>
      {isVisible ? (
        <div ref={ref} className="absolute top-12 left-0 z-10 bg-white rounded-lg shadow w-60 dark:bg-gray-700">
          <div className="p-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MagnifyingGlassSolid width={16} height={16} className="fill-current" />
              </div>
              <input
                type="text"
                id="input-group-search"
                className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                placeholder={t('common.search-country')}
                value={filter}
                onChange={e => setFilter(e.currentTarget.value)}
              />
            </div>
          </div>
          <ul
            className="h-48 px-3 pb-3 overflow-y-auto text-sm text-gray-700 dark:text-gray-200"
            aria-labelledby="dropdownSearchButton"
          >
            {defaultCountries
              .filter(c => c[0].toLowerCase().includes(debouncedFilter.toLowerCase()))
              .map(country => (
                <li key={country[1]}>
                  <button
                    type="button"
                    className="w-full flex items-center pl-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => {
                      setCountry(country[1]);
                      setIsVisible(false);
                    }}
                  >
                    <FlagEmoji iso2={country[1]} />
                    <span className="py-2 ml-2 text-sm font-medium text-gray-900 rounded dark:text-gray-300 text-left">
                      {country[0]} +{country[2]}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
